import { Box, Flex, Spinner, Text, useColorModeValue } from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "moment/locale/id";
import "react-big-calendar/lib/css/react-big-calendar.css";
import scheduleService from "services/report/scheduleService.js";

moment.locale("id");
const localizer = momentLocalizer(moment);

const unwrapList = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.data?.data)) return d.data.data;
  return [];
};

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const personilNames = (row) => {
  const list = Array.isArray(row?.personil) ? row.personil : [];
  if (list.length === 0) return "";
  return list.map((p) => p?.NAMA || p?.nama || p?.NIP || "").filter(Boolean).join(", ");
};

export default function KalenderView() {
  const textColor = useColorModeValue("gray.700", "white");
  const subColor = useColorModeValue("gray.500", "gray.400");
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const settled = await Promise.allSettled([
          scheduleService.getJadwal(1, 200),
          scheduleService.getInspeksi(1, 200),
          scheduleService.getSKP(1, 200),
          scheduleService.getSurveillance(1, 200),
        ]);
        if (!alive) return;
        const merged = new Map();
        settled.forEach((s) => {
          if (s.status === "fulfilled") {
            unwrapList(s.value).forEach((r) => {
              const key = r?.ID ?? `${r?.NIP}-${r?.TANGGAL}-${r?.NM_UPI}`;
              if (!merged.has(key)) merged.set(key, r);
            });
          }
        });
        setRows([...merged.values()]);
        if (merged.size === 0) setError("Belum ada data jadwal lapangan.");
      } catch (e) {
        if (alive) setError("Gagal memuat jadwal lapangan.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const events = useMemo(
    () =>
      rows
        .map((r) => {
          const start = toDate(r.TANGGAL);
          if (!start) return null;
          const endBase = toDate(r.TANGGAL_KEMBALI) || toDate(r.tanggal_kembali) || start;
          const end = new Date(endBase.getTime() + 60 * 60 * 1000);
          const nama = personilNames(r);
          return {
            title: `${r.NM_UPI || r.TUJUAN || "Jadwal"}${nama ? ` — ${nama}` : ""}`,
            start,
            end,
            allDay: true,
            resource: r,
          };
        })
        .filter(Boolean),
    [rows]
  );

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card p={4}>
        <Text fontSize="lg" color={textColor} fontWeight="bold">
          Kalender Jadwal Lapangan
        </Text>
        <Text fontSize="sm" color={subColor} mb={4}>
          Tampilan bulanan / mingguan seluruh penugasan lapangan ({events.length} agenda)
        </Text>
        {loading ? (
          <Flex justify="center" py={10}>
            <Spinner />
          </Flex>
        ) : (
          <Box minH="600px">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              defaultView="month"
              views={["month", "week", "agenda"]}
              style={{ height: 600 }}
              culture="id"
              messages={{
                month: "Bulan",
                week: "Minggu",
                agenda: "Agenda",
                today: "Hari ini",
                previous: "Sebelumnya",
                next: "Berikutnya",
                noEventsInRange: "Tidak ada jadwal pada rentang ini.",
              }}
            />
          </Box>
        )}
        {error && !loading && (
          <Text color="orange.500" fontSize="sm" mt={3}>
            {error}
          </Text>
        )}
      </Card>
    </Flex>
  );
}
